import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ShieldAlert } from 'lucide-react';
import { Button, Card, EmptyState, Skeleton, toast } from '@/ui';
import { isOrgAdmin } from '@/auth/types';
import { useSessionStore } from '@/auth/session.store';
import { LEGAL_COPY } from '@/features/legal/legal-copy';
import {
  fetchMemberPermissions,
  fetchMembers,
  fetchPermissionCatalogue,
  replaceMemberPermissions,
} from './api';
import { PermissionPicker } from './PermissionPicker';
import { organizationKeys } from './query-keys';
import { iamErrorMessage } from './error-message';
import styles from './Organization.module.css';

/**
 * UYE YETKILERI (`/buro/ekip/:memberId/yetkiler`).
 *
 * YETKI SETI BUTUN OLARAK GONDERILIR — bu ekranin en onemli ozelligi budur.
 * Sunucu once uyenin tum grant'lerini pasif hale getirir, sonra gonderilenleri
 * yazar; yani KUTUSU ISARETLI OLMAYAN HER YETKI GERI ALINIR. Ekran bu yuzden:
 *   1) sunucu setinden bir YEREL TASLAK kurar,
 *   2) kaydedilmemis degisikligi acikca gosterir (dirtyBar),
 *   3) Kaydet'e basildiginda TASLAGIN TAMAMINI gonderir.
 * "Yalniz degiseni gonder" turunden bir kisayol, kaydeden kisinin hic gormedigi
 * bir yetki kaybi uretir.
 *
 * YONETICI UYEYE BU EKRAN ACILMAZ: yonetici ortuk olarak tam yetkilidir ve
 * sunucu onun icin grant satiri yazmaz (ADMIN_HAS_IMPLICIT_PERMISSIONS).
 */

const sameSet = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && [...a].sort().join(' ') === [...b].sort().join(' ');

export function MemberPermissionsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const snapshot = useSessionStore((state) => state.snapshot);
  const admin = isOrgAdmin(snapshot);

  // pg int8 -> STRING. Rota parametresi zaten string; Number()'a HIC cevrilmiyor.
  const { memberId = '' } = useParams<{ memberId: string }>();

  const [draft, setDraft] = useState<string[] | null>(null);

  const catalogue = useQuery({
    queryKey: organizationKeys.permissionCatalogue(),
    queryFn: fetchPermissionCatalogue,
    enabled: admin,
    // Katalog neredeyse hic degismez; her acilista yeniden cekmek gereksiz.
    staleTime: 10 * 60_000,
  });

  const granted = useQuery({
    queryKey: organizationKeys.memberPermissions(memberId),
    queryFn: () => fetchMemberPermissions(memberId),
    enabled: admin && memberId !== '',
  });

  // Uye adi icin ayri bir uc YOK; liste sorgusu zaten onbellekte olabilir.
  const members = useQuery({
    queryKey: organizationKeys.members(),
    queryFn: fetchMembers,
    enabled: admin,
  });
  const member = members.data?.find((row) => String(row.id) === String(memberId));

  const save = useMutation({
    mutationFn: (permissionCodes: readonly string[]) =>
      replaceMemberPermissions({ memberId, permissionCodes }),
    onSuccess: async (_result, permissionCodes) => {
      toast({ message: t('org.permissions.saved'), tone: 'success' });
      setDraft([...permissionCodes]);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: organizationKeys.memberPermissions(memberId) }),
        // Uye satirindaki "n yetki tanimli" sayaci bu cagriyla bayatlar.
        queryClient.invalidateQueries({ queryKey: organizationKeys.members() }),
        queryClient.invalidateQueries({ queryKey: organizationKeys.auditLogRoot() }),
      ]);
    },
    onError: (error: unknown) => {
      toast({ message: iamErrorMessage(error, t), tone: 'danger' });
    },
  });

  if (!admin) {
    return (
      <Card padding="lg">
        <EmptyState
          tone="error"
          icon={<ShieldAlert size={28} aria-hidden="true" />}
          title={t('org.onlyAdmins')}
        />
      </Card>
    );
  }

  const server = granted.data;

  if (granted.isPending || catalogue.isPending) {
    return (
      <Card padding="lg">
        <Skeleton shape="block" height="280px" />
      </Card>
    );
  }

  if (granted.isError || server === undefined) {
    return (
      <Card padding="lg">
        <EmptyState
          tone="error"
          title={t('org.permissions.saveFailed')}
          description={iamErrorMessage(granted.error, t)}
          action={
            <Button
              tone="neutral"
              onClick={() => {
                void granted.refetch();
              }}
            >
              {t('common.retry')}
            </Button>
          }
        />
      </Card>
    );
  }

  // Taslak henuz kurulmadiysa sunucu seti gecerlidir. `useEffect` ile
  // senkronize edilmiyor: render sirasinda turetmek, "sunucu yaniti gelince
  // kullanicinin secimleri silindi" yarisini bastan imkansiz kiliyor.
  const selected = draft ?? server;
  const dirty = !sameSet(selected, server);

  const toggle = (code: string): void => {
    setDraft(
      selected.includes(code) ? selected.filter((item) => item !== code) : [...selected, code],
    );
  };

  return (
    <div className={styles.page}>
      <Card padding="md">
        <div className={styles.pageHead}>
          <div>
            <h1 className={styles.title}>{member?.userFullName ?? t('members.unknownUser')}</h1>
            <p className={styles.subtitle}>{member?.userEmail ?? ''}</p>
          </div>
          <Link to="/buro/ekip" className={styles.linkButton}>
            <ArrowLeft size={16} aria-hidden="true" />
            {t('org.invite.backToMembers')}
          </Link>
        </div>
      </Card>

      <p className={styles.notice}>{t('org.permissions.hint')}</p>

      {dirty ? (
        <div className={styles.dirtyBar}>
          <span>{t('org.permissions.unsaved')}</span>
          <Button
            tone="ghost"
            size="sm"
            onClick={() => {
              setDraft(null);
            }}
          >
            {t('org.permissions.discard')}
          </Button>
        </div>
      ) : null}

      <Card padding="lg">
        {catalogue.data && catalogue.data.length > 0 ? (
          <PermissionPicker
            catalogue={catalogue.data}
            selected={selected}
            onToggle={toggle}
            disabled={save.isPending}
          />
        ) : (
          <EmptyState tone="error" title={t('org.permissions.catalogueFailed')} />
        )}
      </Card>

      {/* Personelin izlendigini BILMESI, mesru menfaat dayanaginin on kosulu. */}
      <p className={styles.notice}>{LEGAL_COPY.organization.auditNotice}</p>

      <div className={styles.formActions}>
        <Button
          loading={save.isPending}
          disabled={!dirty}
          onClick={() => {
            // TAM SET gonderilir — sunucu semantigi REPLACE.
            save.mutate(selected);
          }}
        >
          {save.isPending ? t('org.profile.saving') : t('org.profile.save')}
        </Button>
        <Button
          tone="ghost"
          onClick={() => {
            void navigate('/buro/ekip');
          }}
        >
          {t('auth.give')}
        </Button>
      </div>
    </div>
  );
}
